import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import RecyclingIcon from "@mui/icons-material/Recycling";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useDialog from "../../../hooks/useDialog";
import DateService from "../../../services/dateService";
import AddEditDocsRestrictedDialog from "../../dialog/AddEditDocsRestrictedDialog/AddEditDocsRestrictedDialog";
import ConfirmationDialog from "../../dialog/ConfirmationDialog/confirmationDialog";

function FileDataRow(props) {
    const { t } = useTranslation();
    const [isRevisionMode, setIsRevisionMode] = useState(false);

    const isShowPreview =
        props.file.docs_content_type === "application/pdf" ||
        props.file.content_type === "application/pdf";

    const canBeRemoved = (props.canRemoveFiles && !props.readOnly) || props.file.id === undefined;
    const miniatureUrl = props.file.id ? props.file.miniature_download_link : props.file?.blob_url;

    const getFileName = () => {
        switch (props.fileType) {
            case "docs":
                return props.file.docs_name;
            default:
                return props.file.file_name;
        }
    };
    const fileName = getFileName();

    const [openNotesDialog, onOpenNotesDialog, onCloseNotesDialog] = useDialog();

    const [openConfimartionDialog, onOpenConfimartionDialog, onCloseConfimartionDialog] =
        useDialog();

    const downloadFile = useCallback(() => {
        const file_name = fileName;
        props.onDownload(props.file.id, file_name, props.index);
    }, [props.file, props.index, props.fileType]);

    const previewFile = useCallback(() => {
        props.onPreview(props.file.id, props.index);
    }, [props.file, props.index, props.fileType]);

    const deleteFile = useCallback(() => {
        props.onDeleteFile(props.index);
        onCloseConfimartionDialog();
    }, [props.file, props.index]);

    const onCloseDocsDialog = useCallback(() => {
        setIsRevisionMode(false);
        onCloseNotesDialog();
        if (props.onRefetch) {
            props.onRefetch();
        }
    });

    const onOpenRevisionDialog = useCallback((e) => {
        e.stopPropagation();
        setIsRevisionMode(true);
        onOpenNotesDialog();
    }, []);

    return (
        <>
            <Grid container justifyContent="flex-start" alignItems="flex-start">
                {props.showCreator && (
                    <>
                        <Grid item xs={12} sm={1} />
                        <Grid item xs={12} sm={11} sx={{ padding: "0", textAlign: "left" }}>
                            <Typography variant="caption">
                                {" "}
                                {props.file?.user_full_name}:
                            </Typography>
                        </Grid>
                    </>
                )}
                <Grid item xs={12} sm={1}>
                    <Typography variant="body2" sx={{ padding: "5px", textAlign: "center" }}>
                        {" "}
                        {props.index + 1}{" "}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography variant="body2" sx={{ padding: "5px", textAlign: "left" }}>
                        {" "}
                        {fileName}{" "}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={0.5} />

                {props.showDetailsButton ? (
                    <Grid item xs={12} sm={0.5}>
                        <Tooltip title={t("other.file_data_row.details")}>
                            <IconButton
                                color="primary"
                                onClick={onOpenNotesDialog}
                                sx={{ padding: "5px" }}
                            >
                                <InfoIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                ) : (
                    <Grid item xs={12} sm={0.5} />
                )}
                {props.showDownloadButton ? (
                    <Grid item xs={12} sm={0.5}>
                        <Tooltip title={t("other.file_data_row.download")}>
                            <IconButton
                                color="primary"
                                onClick={downloadFile}
                                sx={{ padding: "5px" }}
                            >
                                <DownloadIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                ) : (
                    <Grid item xs={12} sm={0.5} />
                )}
                <Grid item xs={12} sm={0.5}>
                    {isShowPreview && (
                        <Tooltip title={t("other.file_data_row.preview")}>
                            <IconButton
                                color="primary"
                                onClick={previewFile}
                                sx={{ padding: "5px" }}
                            >
                                <PreviewOutlinedIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isShowPreview && miniatureUrl ? (
                        <Tooltip title={t("other.file_data_row.preview")}>
                            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <legacy> */}
                            <img
                                id="pictureId"
                                src={miniatureUrl}
                                width="70%"
                                height="70%"
                                alt={""}
                                onClick={previewFile}
                            />
                        </Tooltip>
                    ) : (
                        <></>
                    )}
                </Grid>
                <Grid item xs={12} sm={0.5}>
                    {canBeRemoved && (
                        <Tooltip title={t("other.file_data_row.delete")}>
                            <IconButton
                                color="primary"
                                onClick={onOpenConfimartionDialog}
                                sx={{ padding: "5px" }}
                            >
                                <DeleteIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
                <Grid item xs={12} sm={0.5}>
                    {props.showRevisionButton && (
                        <Tooltip title={t("other.file_table_cell.revision")}>
                            <IconButton
                                color="primary"
                                onClick={onOpenRevisionDialog}
                                sx={{ padding: "5px" }}
                            >
                                <RecyclingIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
                <Grid item xs={12} sm={7} sx={{ textAlign: "end" }}>
                    <Typography variant="caption" sx={{ padding: "0", textAlign: "end" }}>
                        {" "}
                        {DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
                            props.file?.created,
                        )}
                    </Typography>
                </Grid>
            </Grid>
            {openNotesDialog && props.fileType === "docs" && (
                <AddEditDocsRestrictedDialog
                    open={openNotesDialog}
                    onClose={onCloseNotesDialog}
                    docsId={props.file.id}
                    docInvestId={props.file.docinvest_id}
                    docsStyleId={props.file.docsstyle_id}
                    docsPartId={props.file.docspart_id}
                    submitButtonLabel={t("dialog.file_dialog.edit_docs")}
                    readOnly={props.readOnly}
                    onSubmit={onCloseDocsDialog}
                    availableDocsRestrictedTypes={props.availableDocsRestrictedTypes}
                    isRevisionMode={isRevisionMode}
                    multiple={props.multiple && !isRevisionMode}
                    showStandards={props.showStandards}
                />
            )}

            {openConfimartionDialog && (
                <ConfirmationDialog
                    open={openConfimartionDialog}
                    onYes={deleteFile}
                    onNo={onCloseConfimartionDialog}
                    title={t(`other.file_data_row.remove_${props.fileType}`)}
                    content={t(`other.file_data_row.are_you_sure_to_remove_${props.fileType}`)}
                    maxWidth={"xs"}
                    titleSx={{ color: "var(--alert)" }}
                    contentSx={{ fontFamily: "Roboto", textAlign: "center" }}
                    btnYesColor={"error"}
                    btnNoColor={"secondary"}
                    showDialogContentDividers={false}
                />
            )}
        </>
    );
}

FileDataRow.propTypes = {
    enclosure: PropTypes.object,
    index: PropTypes.number,
    onDownload: PropTypes.func,
    onPreview: PropTypes.func,
    onAddEnclosureToFavorite: PropTypes.func,
    showSubcontractorSelect: PropTypes.bool,
    showEnclosureType: PropTypes.bool,
    onUpdateEnclosure: PropTypes.func,
    showCreator: PropTypes.bool,
    readOnly: PropTypes.bool,
    onCopyEnclosure: PropTypes.func,
    canHideEnclosures: PropTypes.bool,
    showAddToFavorite: PropTypes.bool,
    showPublishButton: PropTypes.bool,
    showDetailsButton: PropTypes.bool,
    showDownloadButton: PropTypes.bool,
    canRemoveFiles: PropTypes.bool,
    fileType: PropTypes.string,
    showRevisionButton: PropTypes.bool,
};

FileDataRow.defaultProps = {
    enclosure: {},
    index: 1,
    showSubcontractorSelect: false,
    showEnclosureType: false,
    showCreator: true,
    readOnly: false,
    canHideEnclosures: false,
    showAddToFavorite: true,
    showPublishButton: true,
    showDetailsButton: true,
    showDownloadButton: true,
    showHideIcon: true,
    canRemoveFiles: false,
    fileType: "enclosure",
    showRevisionButton: false,
    showStandards: false,
};

export default FileDataRow;
